import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicShell from "@/components/PublicShell";
import { api } from "@/lib/api";
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function fmtMoney(v, c = "USD") {
  try {
    return `$${Number(v).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${c}`;
  } catch {
    return `$${v} ${c}`;
  }
}

export default function VerifyReceipt() {
  const [params, setParams] = useSearchParams();
  const [tx, setTx] = useState(params.get("tx") || "");
  const [sig, setSig] = useState(params.get("sig") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { valid, reason?, ...tx }
  const [error, setError] = useState(null);

  const runCheck = async (txId, sigVal) => {
    if (!txId || !sigVal) {
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/wallet/verify-receipt?tx_id=${encodeURIComponent(
          txId
        )}&sig=${encodeURIComponent(sigVal)}`
      );
      setResult(data);
    } catch (e) {
      setError("Could not reach the verification service. Please try again.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-check on mount if params are present
  useEffect(() => {
    const t = params.get("tx");
    const s = params.get("sig");
    if (t && s) runCheck(t, s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const t = tx.trim();
    const s = sig.trim().toUpperCase();
    setParams({ tx: t, sig: s }, { replace: true });
    runCheck(t, s);
  };

  return (
    <PublicShell>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="text-eyebrow text-[#4F4D45]">Receipt authenticity</div>
        <div className="flex items-center gap-3 mt-2">
          <ShieldCheck className="w-8 h-8 text-[#1B3526]" />
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            Verify a FinVault receipt
          </h1>
        </div>
        <p className="text-[#4F4D45] mt-4 max-w-2xl">
          Every FinVault receipt is signed with a tamper-evident fingerprint.
          Scan the QR code on the PDF, or paste the transaction ID and
          signature below to confirm it was issued by us and hasn't been
          modified.
        </p>

        {/* Input card */}
        <form
          onSubmit={handleSubmit}
          className="mt-10 rounded-2xl border border-[#D2CDBE] bg-white p-6 md:p-8 space-y-4"
          data-testid="verify-form"
        >
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4F4D45] font-semibold">
              Transaction ID
            </label>
            <Input
              value={tx}
              onChange={(e) => setTx(e.target.value)}
              placeholder="e.g. 284003ec54ecdfc6"
              className="mt-1 font-mono"
              data-testid="verify-input-tx"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4F4D45] font-semibold">
              Signature (12 characters, from the QR code or footer)
            </label>
            <Input
              value={sig}
              onChange={(e) => setSig(e.target.value.toUpperCase())}
              placeholder="e.g. A9E3F2B10D4C"
              className="mt-1 font-mono uppercase tracking-widest"
              maxLength={12}
              data-testid="verify-input-sig"
            />
          </div>
          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-[#4F4D45]">
              We compare your inputs against the transaction on file using an
              HMAC-SHA256 signature that only FinVault can produce.
            </p>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#1B3526] hover:bg-[#0F2517] text-white shrink-0"
              data-testid="verify-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" /> Verify
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Result panel */}
        {error && (
          <div
            className="mt-6 rounded-2xl border border-[#E27A6A]/50 bg-[#E27A6A]/10 p-5 text-sm text-[#0A0A08]"
            data-testid="verify-error"
          >
            {error}
          </div>
        )}

        {result && !error && (
          <div
            className={`mt-6 rounded-2xl border p-6 md:p-8 ${
              result.valid
                ? "border-[#4E7F5F]/30 bg-[#4E7F5F]/8"
                : "border-[#B85847]/40 bg-[#B85847]/10"
            }`}
            data-testid="verify-result"
          >
            <div className="flex items-start gap-3">
              {result.valid ? (
                <CheckCircle2
                  className="w-8 h-8 text-[#1B3526] shrink-0"
                  data-testid="verify-badge-valid"
                />
              ) : (
                <ShieldAlert
                  className="w-8 h-8 text-[#B85847] shrink-0"
                  data-testid="verify-badge-invalid"
                />
              )}
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0A0A08]">
                  {result.valid
                    ? "This receipt is authentic."
                    : result.reason === "tampered"
                    ? "This receipt cannot be verified."
                    : "No transaction found."}
                </h2>
                <p className="text-[#4F4D45] mt-2 leading-relaxed">
                  {result.valid
                    ? "The transaction ID and signature match a record issued by FinVault. The details below confirm the transaction as it exists in our ledger."
                    : result.reason === "tampered"
                    ? "The signature does not match this transaction ID. The PDF may have been altered or the values were mistyped."
                    : "We have no record of a transaction with this ID. Double-check the ID from your PDF or QR code."}
                </p>
              </div>
            </div>

            {result.valid && (
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <DetailRow label="Transaction ID" mono>
                  {result.tx_id}
                </DetailRow>
                <DetailRow label="Type">{result.type_label}</DetailRow>
                <DetailRow label="Amount">
                  <span className="font-display text-xl text-[#1B3526]">
                    {fmtMoney(result.amount, result.currency)}
                  </span>
                </DetailRow>
                <DetailRow label="Mode">
                  {(result.mode || "").toUpperCase() || "—"}
                </DetailRow>
                <DetailRow label="Status">
                  {(result.status || "").replace("_", " ").toUpperCase()}
                </DetailRow>
                <DetailRow label="Owner">
                  {result.owner_name || "—"}
                  {result.owner_email_masked && (
                    <div className="text-xs text-[#4F4D45] mt-0.5">
                      {result.owner_email_masked}
                    </div>
                  )}
                </DetailRow>
                {result.counterparty_name && (
                  <DetailRow label="Counterparty" wide>
                    {result.counterparty_name}
                  </DetailRow>
                )}
                <DetailRow label="Created">
                  {result.created_at
                    ? new Date(result.created_at).toLocaleString()
                    : "—"}
                </DetailRow>
                {result.completed_at && (
                  <DetailRow label="Completed">
                    {new Date(result.completed_at).toLocaleString()}
                  </DetailRow>
                )}
              </div>
            )}

            {!result.valid && result.reason === "tampered" && (
              <div className="mt-6 rounded-lg bg-white border border-[#D2CDBE] p-4 text-sm text-[#4F4D45]">
                If you believe this is a legitimate FinVault receipt that has
                been altered, contact{" "}
                <a
                  href="mailto:support@finvault.capital"
                  className="text-[#1B3526] underline"
                >
                  support@finvault.capital
                </a>{" "}
                with a copy of the PDF and details of where you received it.
              </div>
            )}
          </div>
        )}

        {/* Educational panel */}
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          <InfoCard
            title="Signed by FinVault"
            body="Each receipt is signed with HMAC-SHA256 using our server-side JWT secret. Only FinVault can produce the signature."
          />
          <InfoCard
            title="Tamper-evident"
            body="Changing the amount, tx ID, or currency on the PDF invalidates the signature. The QR code on every receipt takes you here."
          />
          <InfoCard
            title="Non-custodial by design"
            body={
              <>
                Learn more about how we protect your money on our{" "}
                <Link to="/about" className="text-[#1B3526] underline">
                  About
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-[#1B3526] underline">
                  Privacy
                </Link>{" "}
                pages.
              </>
            }
          />
        </div>

        <div className="mt-10 text-center text-sm">
          <Link
            to="/help"
            className="inline-flex items-center gap-1 text-[#1B3526] hover:underline"
          >
            Question about a receipt? Contact support{" "}
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}

function DetailRow({ label, children, mono, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-[#4F4D45] font-semibold">
        {label}
      </div>
      <div
        className={`mt-1 text-[#0A0A08] ${
          mono ? "font-mono text-sm" : "text-base"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function InfoCard({ title, body }) {
  return (
    <div className="rounded-2xl border border-[#D2CDBE] bg-[#F0ECE0]/60 p-5">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#1B3526]" />
        <div className="font-display font-semibold text-[#0A0A08]">{title}</div>
      </div>
      <div className="text-sm text-[#4F4D45] mt-2 leading-relaxed">{body}</div>
    </div>
  );
}
